/**
 * MOTYWY SEZONOWE — KONTRAKT KLIENTA.
 *
 * <b>Po co to istnieje.</b> Właściciel ma móc podmienić dynię na czapkę
 * Mikołaja bez wydawania nowej wersji w sklepach. Aplikacja zna więc
 * z góry SKOŃCZONY zestaw miejsc, typów zasobów i animacji, a zdalna
 * konfiguracja może tylko WYBIERAĆ spośród nich i podać adres obrazka.
 *
 * <b>Granica bezpieczeństwa.</b> Ten plik jest jedynym miejscem, w którym
 * definiujemy, co zdalna konfiguracja w ogóle może powiedzieć. Wszystko
 * poza tymi wyliczeniami jest odrzucane przez walidator — nie „ignorowane
 * przy renderowaniu", tylko odrzucane przed nim. Konfiguracja NIE JEST
 * kodem: nie ma tu pola na HTML, na CSS ani na nazwę funkcji.
 *
 * <b>Czego tu nie ma.</b> Nazw pól odpowiedzi backendu. Walidator przyjmuje
 * `unknown` i sam wyciąga to, co rozumie, więc backend może ustalić
 * równoważny kontrakt bez przepisywania klienta.
 */

import { EFEKTY, REGIONY, Scena, TYPY_WARSTW } from './scena.model';

/** Miejsca, w których dekoracja może się pojawić. Zamknięta lista. */
export const MIEJSCA = [
  'LOGO_BADGE',
  'HEADER_DECORATION',
  'HOME_TOP_DECORATION',
  'HOME_BACKGROUND_OVERLAY',
  'MENU_DECORATION',
  'BOTTOM_NAV_DECORATION',
  'EMPTY_STATE_DECORATION',
  'SEASONAL_BANNER',
  // 2026-09-29 (wzory OWNERA): ozdoby ZAKOTWICZONE do elementów UI — scena
  // nagłówka stoi przy prawej krawędzi, więc „ostrokrzew tuż za napisem"
  // odjeżdżał od logo o kilkadziesiąt px na szerszych ekranach. Starsza
  // wersja aplikacji odrzuca nieznane miejsce POJEDYNCZO (walidator:
  // „odrzucanie częściowe"), resztę motywu rysuje — zgodność zachowana.
  'LOGO_SIDE',
  'SECTION_ACCENT',
  // 2026-09-29 (OWNER, layout okazji — pkt J): DUŻA GRAFIKA OKOLICZNOŚCIOWA
  // w PRZEPŁYWIE strony, pod żółtym paskiem, nad „Restauracjami". Jedyne
  // miejsce, które zajmuje wysokość — i to tylko przy aktywnej grafice.
  'EVENT_HERO',
  // 2026-09-29 (OWNER, pkt G/K): SCENA NA DOLE MENU, za ostatnią
  // sekcją (np. panorama miasta za „Social media"). Pod treścią sekcji,
  // zakotwiczona w prawym dolnym rogu — kafle zawsze rysują się nad nią.
  'FOOTER_SCENE',
  // 2026-10-08 (OWNER, skórki 1:1 wg wzorów z pliku ZIP) — SILNIK 2.
  // TŁO NAGŁÓWKA: warstwa na CAŁĄ ciemną część nagłówka (z wcięciem paska
  // statusu). Obrazek dostaje rozmiar warstwy, a układ ozdób trzyma sam plik
  // SVG (procenty, zagnieżdżone `<svg x="100%">`, zapytania o wysokość) —
  // ozdoby przy logo, przy zegarze i w rogu stoją na swoich miejscach przy
  // każdej szerokości i każdym wcięciu. Pod logo, datą, zegarem i paskiem.
  'HEADER_BACKDROP',
  // TŁO TREŚCI: warstwa za pierwszymi sekcjami menu (gałązki w rogu pod
  // paskiem, delikatne gwiazdki, szron). Pod kaflami, nad tłem ekranu.
  'CONTENT_BACKDROP',
  // AKCENT TYTUŁU SEKCJI PO PRAWEJ: przy „Pokaż wszystkie", w prawym marginesie.
  'SECTION_ACCENT_RIGHT',
] as const;
export type Miejsce = (typeof MIEJSCA)[number];

/**
 * Miejsca WYPEŁNIAJĄCE (silnik 2): obrazek ma rozmiar swojej warstwy
 * (`width/height: 100%`), a nie własny. Plik SVG układa się sam względem
 * tej warstwy, więc NIE ma rozmiaru wewnętrznego (`width="100%"`).
 */
export const MIEJSCA_WYPELNIAJACE: readonly Miejsce[] = [
  'HEADER_BACKDROP',
  'CONTENT_BACKDROP',
];

/**
 * Miejsca W PRZEPŁYWIE strony: dekoracja ma własną wysokość (proporcje
 * obrazka), zamiast leżeć w warstwie absolutnej nad/pod treścią. Brak
 * dekoracji = zero elementów = zero wysokości (bez „dziury").
 */
export const MIEJSCA_W_PRZEPLYWIE: readonly Miejsce[] = ['EVENT_HERO'];

/** Rodzaje zasobów, które klient umie bezpiecznie narysować. */
export const RODZAJE_ZASOBU = [
  'STATIC_IMAGE',
  'ANIMATED_IMAGE',
  'LIGHTWEIGHT_VECTOR_ANIMATION',
  'CSS_PRESET_DECORATION',
] as const;
export type RodzajZasobu = (typeof RODZAJE_ZASOBU)[number];

/** Animacje. Presety, nie dowolność. */
export const PRESETY = [
  'NONE',
  'FLOAT',
  'SWAY',
  'PULSE',
  'FALLING',
  'SNOW',
  'FADE',
  'BOUNCE_SUBTLE',
  // 2026-09-29 (OWNER, „remote skins bez aktualizacji" pkt 6): połysk i
  // poświata jako EFEKTY silnika, a nie tylko ruch zaszyty w pliku SVG.
  'SHIMMER',
  'GLOW',
] as const;
export type Preset = (typeof PRESETY)[number];

/**
 * WERSJA SILNIKA MOTYWÓW (OWNER 2026-09-29, „remote skins" pkt 7).
 *
 * Motyw może zadeklarować `compatibility.minEngineVersion` oraz listę
 * `compatibility.requires` (nazwy zdolności). Aplikacja, której silnik jest
 * starszy albo która nie zna choćby jednej wymaganej zdolności, NIE rysuje
 * motywu wcale — zwykły interfejs, żadnej „połowy skórki". Podnosimy tę
 * liczbę przy KAŻDEJ zmianie, która dodaje zdolność (miejsce, efekt, pole).
 *
 * 1 = pierwsza wersja wydana w 4.0: miejsca, rodzaje zasobów i presety
 *     z tego pliku + messageBar, typography(UPPERCASE), palette, hero, effects.
 */
export const WERSJA_SILNIKA = 3;
// 2 = (2026-10-08) HEADER_BACKDROP, CONTENT_BACKDROP, SECTION_ACCENT_RIGHT,
//     ikona żółtego paska (`messageBar.icon`), hero na pełną szerokość,
//     bliźniak statyczny zasobu (`staticUrl`).
// 3 = (2026-10-08) UNIWERSALNY SILNIK — graf sceny (schemaVersion 3). To jest
//     OSTATNI skok tej liczby: służy wyłącznie temu, żeby aplikacje ze sklepu
//     (silnik 1, 4.0.0–4.0.9) odrzuciły skórki grafu sceny i pokazały zwykłe UI.
//     Dalsze rozszerzenia wersjonuje `UNIWERSALNY_SILNIK`
//     (`compatibility.minUniversalEngine`) — patrz `scena.model.ts`.

/** Stan cyklu życia motywu — pod przyszły panel administracyjny. */
export const STANY_MOTYWU = [
  'DRAFT',
  'SCHEDULED',
  'ACTIVE',
  'DISABLED',
] as const;
export type StanMotywu = (typeof STANY_MOTYWU)[number];

/**
 * OGRANICZENIA MIEJSC.
 *
 * Każde miejsce ma własny sufit rozmiaru, własny zakres `z-index`
 * i własny limit liczby elementów cząsteczkowych. To nie jest kosmetyka:
 * bez sufitu liczby elementów zdalna konfiguracja mogłaby poprosić
 * o 500 płatków śniegu i zatrzymać telefon.
 *
 * `warstwa` trzyma się PONIŻEJ paska zakładek (1000) i modali Ionica
 * (10000+), żeby dekoracja nigdy nie przykryła nawigacji ani alertu.
 */
export interface OgraniczeniaMiejsca {
  /** Maksymalna szerokość pojedynczego elementu dekoracji. */
  readonly maxSzerokosc: number;
  /** Maksymalna wysokość pojedynczego elementu dekoracji. */
  readonly maxWysokosc: number;
  /** Zakres warstwy — dekoracja nigdy nie wychodzi ponad interfejs. */
  readonly warstwa: number;
  /** Sufit liczby elementów dla presetów cząsteczkowych. */
  readonly maxElementow: number;
  /** Czy to miejsce jest tłem rozciągniętym na ekran. */
  readonly overlay: boolean;
}

export const OGRANICZENIA: Readonly<Record<Miejsce, OgraniczeniaMiejsca>> = {
  // AKCENT NA DYMKU „Tu" (OWNER 2026-09-28, skórki od nowa).
  //
  // Dotąd znaczek leżał NA logo w lewym górnym rogu i zasłaniał „Tu"
  // (dynia 34 px, czapka jako czerwona plama). Teraz stoi NAD dymkiem:
  // dolna krawędź dotyka górnego brzegu dymku, a litery zostają wolne.
  // Rozmiar dopasowany do dymku (21×18 px), nie do całego logo.
  LOGO_BADGE: {
    maxSzerokosc: 30,
    maxWysokosc: 26,
    warstwa: 3,
    maxElementow: 1,
    overlay: false,
  },
  // SCENA NAGŁÓWKA (OWNER 2026-09-28).
  //
  // Dotąd miejsce na ikonkę 120×56 w pasku logo — stąd efekt „kilka
  // przypadkowych płaskich ozdobników". Teraz jedna KOMPONOWANA ilustracja
  // na całą ciemną część nagłówka (412×89 w projekcie), zakotwiczona
  // w prawym górnym rogu i położona POD treścią nagłówka (`warstwa: -1`):
  // logo, data, zegar i ikony zawsze rysują się nad nią, więc scena nie
  // może niczego zasłonić ani odebrać dotknięcia. Ruch (migotanie
  // światełek, płomień w dyni) żyje WEWNĄTRZ pliku SVG — deklaratywnie,
  // bez skryptów, bo `<img>` ich nie wykonuje.
  HEADER_DECORATION: {
    maxSzerokosc: 480,
    maxWysokosc: 96,
    warstwa: -1,
    maxElementow: 6,
    overlay: false,
  },
  HOME_TOP_DECORATION: {
    maxSzerokosc: 320,
    maxWysokosc: 120,
    warstwa: 1,
    maxElementow: 8,
    overlay: false,
  },
  HOME_BACKGROUND_OVERLAY: {
    maxSzerokosc: 64,
    maxWysokosc: 64,
    // ZMIERZONE 2026-09-20 (OWNER: „widok sezonowych grafik nie działa"):
    // z `warstwa: 0` warstwa cząstek (śnieg, serca, płatki) leżała POD
    // nieprzezroczystymi powierzchniami ekranu — `elementFromPoint` w środku
    // każdej cząstki trafiał w `ion-header` / `tsz-header__bar` / zdjęcie
    // kafla, więc 12 animowanych płatków istniało w DOM i nie było widać
    // ani jednego. Overlay ma być NAD treścią ekranu (`ion-router-outlet`
    // stoi na 0), a POD paskiem zakładek (1000), toastami i modalami
    // Ionica (20000+); `pointer-events: none` nadal — nie łapie dotknięć.
    warstwa: 9,
    maxElementow: 24,
    overlay: true,
  },
  MENU_DECORATION: {
    maxSzerokosc: 160,
    maxWysokosc: 96,
    warstwa: 1,
    maxElementow: 6,
    overlay: false,
  },
  BOTTOM_NAV_DECORATION: {
    maxSzerokosc: 220,
    maxWysokosc: 40,
    warstwa: 1,
    maxElementow: 6,
    overlay: false,
  },
  EMPTY_STATE_DECORATION: {
    maxSzerokosc: 200,
    maxWysokosc: 200,
    warstwa: 1,
    maxElementow: 4,
    overlay: false,
  },
  SEASONAL_BANNER: {
    maxSzerokosc: 640,
    maxWysokosc: 132,
    warstwa: 1,
    maxElementow: 1,
    overlay: false,
  },
  // TUŻ ZA LOGO: obrazek zaczyna się 4 px za prawą krawędzią logo i jest
  // wyśrodkowany w pionie na logo. Po prawej jest tylko pusty odstęp
  // paska (ikony stoją przy prawej krawędzi ekranu), więc 48 px mieści się
  // bez dotykania czegokolwiek na 360 px.
  LOGO_SIDE: {
    maxSzerokosc: 48,
    maxWysokosc: 34,
    warstwa: 1,
    maxElementow: 1,
    overlay: false,
  },
  // AKCENT TYTUŁU SEKCJI: w LEWYM MARGINESIE (16 px) przy każdym tytule
  // sekcji menu. Szerokość 20 i przesunięcie −6 px: prawa krawędź na 14 px,
  // tytuł zaczyna się na 16 px — akcent NIGDY nie wchodzi na tekst, a na
  // krawędzi ekranu lekko się chowa (jak gałązka na wzorze).
  SECTION_ACCENT: {
    maxSzerokosc: 20,
    maxWysokosc: 28,
    warstwa: 1,
    maxElementow: 1,
    overlay: false,
  },
  // HERO OKAZJI: od krawędzi do krawędzi ekranu, tuż pod żółtym paskiem
  // (wzory OWNERA 2026-10-08: blok bez marginesów i zaokrągleń), wysokość
  // z proporcji obrazka, sufit 270 px. W przepływie — nie nachodzi na
  // karuzele, niczego nie przykrywa, nie jest tłem kart.
  EVENT_HERO: {
    maxSzerokosc: 1200,
    maxWysokosc: 270,
    warstwa: 0,
    maxElementow: 1,
    overlay: false,
  },
  // SCENA STOPKI: pełna szerokość sekcji, dół do dołu sekcji, POD kaflami
  // (`warstwa: 0`, kafle mają 1). Projekt: 412 × 190.
  FOOTER_SCENE: {
    maxSzerokosc: 800,
    maxWysokosc: 260,
    warstwa: 0,
    maxElementow: 1,
    overlay: false,
  },
  // TŁO NAGŁÓWKA (silnik 2): rozmiar = warstwa nagłówka; sufity tylko jako
  // zabezpieczenie (Pro Max 430 × 59 + 89).
  HEADER_BACKDROP: {
    maxSzerokosc: 1000,
    maxWysokosc: 400,
    warstwa: -1,
    maxElementow: 1,
    overlay: false,
  },
  // TŁO TREŚCI (silnik 2): pas od górnej krawędzi treści, pod kaflami.
  CONTENT_BACKDROP: {
    // 720: ozdoby wzorów sięgają trzeciej sekcji (~620 px); wyższa warstwa
    // to większy obraz przerysowywany w każdej klatce ruchu (zmierzone 2026-10-08).
    maxSzerokosc: 1000,
    maxWysokosc: 720,
    warstwa: -1,
    maxElementow: 1,
    overlay: false,
  },
  // AKCENT TYTUŁU PO PRAWEJ (silnik 2): w prawym marginesie nagłówka sekcji.
  SECTION_ACCENT_RIGHT: {
    // prawy margines ma 16 px — akcent nie wchodzi na „Pokaż wszystkie"
    maxSzerokosc: 16,
    maxWysokosc: 28,
    warstwa: 1,
    maxElementow: 1,
    overlay: false,
  },
};

/**
 * TYPOGRAFIA MOTYWU (pkt K: „typography treatment", np. cały interfejs wersalikami).
 * Zamknięta lista; aplikacja zamienia ją na klasę CSS na korzeniu. Pola
 * formularzy i tekst techniczny są wyłączone z przekształcenia.
 */
export const TYPOGRAFIE = ['UPPERCASE'] as const;
export type Typografia = (typeof TYPOGRAFIE)[number];

/**
 * ŻÓŁTY PASEK WIADOMOŚCI przy aktywnej okazji (pkt J): tekst okazji zamiast
 * informacji o koszyku / szkole. Tekst jest TEKSTEM (interpolacja Angulara,
 * nigdy HTML), przycięty do `MAX_ZNAKOW_PASKA`; kolory wyłącznie `#rrggbb`.
 */
export const MAX_ZNAKOW_PASKA = 80;
export interface PasekWiadomosci {
  readonly tekst: string;
  readonly tlo: string | null;
  readonly kolorTekstu: string | null;
  /** Ikona przed tekstem (silnik 2) — adres zatwierdzonego zasobu albo `null`. */
  readonly ikona: string | null;
  /** Wyróżnienie tekstu (schemat 3): pogrubienie / wersaliki — z zamkniętej listy. */
  readonly wyroznienie: 'NONE' | 'BOLD' | 'UPPERCASE';
}

/**
 * PALETA EKRANU MENU (pkt K: „palette"; np. ciemne tło i karty).
 *
 * Zamknięty zestaw pięciu ról, wyłącznie `#rrggbb`. Paleta działa TYLKO na
 * ekranie menu (tam, gdzie żyje motyw) — koszyk, portfel, formularze i
 * modale zostają w barwach aplikacji, więc motyw nie może zepsuć czytelności
 * ekranów, których OWNER nie oglądał. Walidator odrzuca CAŁĄ paletę, jeśli
 * tekst nie ma kontrastu ≥ 4.5:1 z tłem i z kartą (WCAG AA) albo tekst
 * pomocniczy ≥ 3:1 z kartą — brzydka paleta ma dać zwykłe barwy, nie
 * nieczytelne menu.
 */
export const MIN_KONTRAST_TEKSTU = 4.5;
export const MIN_KONTRAST_POMOCNICZEGO = 3;
export interface Paleta {
  /** Tło ekranu menu. */
  readonly tlo: string;
  /** Tło kart dań. */
  readonly karta: string;
  /** Tekst główny (tytuły sekcji, nazwy dań). */
  readonly tekst: string;
  /** Tekst pomocniczy (restauracja, oceny). */
  readonly pomocniczy: string;
  /** Obramowania kart. */
  readonly linia: string;
}

/**
 * ZAKRESY PARAMETRÓW ANIMACJI.
 *
 * Walidator PRZYCINA wartości do tych zakresów zamiast odrzucać całą
 * dekorację. Powód jest praktyczny: literówka w panelu (`speed: 40`)
 * ma dać wolniejszą dynię, a nie zniknięcie motywu w całej flocie.
 * Wartości spoza zakresu są jednak zliczane, żeby dało się je zobaczyć.
 */
export interface ZakresParametru {
  readonly min: number;
  readonly max: number;
  readonly domyslnie: number;
}

export const ZAKRESY: Readonly<Record<string, ZakresParametru>> = {
  speed: { min: 0.25, max: 4, domyslnie: 1 },
  delay: { min: 0, max: 5, domyslnie: 0 },
  density: { min: 1, max: 24, domyslnie: 8 },
  opacity: { min: 0.05, max: 1, domyslnie: 1 },
  scale: { min: 0.25, max: 3, domyslnie: 1 },
  rotation: { min: -180, max: 180, domyslnie: 0 },
  maxItems: { min: 1, max: 24, domyslnie: 8 },
};

export type ParametryAnimacji = {
  [K in keyof typeof ZAKRESY]?: number;
};

/** Zasób po walidacji — adres jest już sprawdzony pod kątem pochodzenia. */
export interface Zasob {
  readonly id: string;
  readonly rodzaj: RodzajZasobu;
  /** Pusty dla `CSS_PRESET_DECORATION` — ten rodzaj nie pobiera pliku. */
  readonly url: string;
  readonly opis?: string;
  /** Deklarowany rozmiar w bajtach, jeśli backend go poda. */
  readonly bajtow?: number;
  /**
   * BLIŹNIAK STATYCZNY (silnik 2, `staticUrl`): ta sama scena BEZ ruchu.
   * Warstwa wypełniająca pokazuje go w spoczynku epizodu i przy ograniczonym
   * ruchu zamiast klatki z płótna — płótno rysuje SVG bez rozmiaru własnego
   * w rozdzielczości CSS (patrz komponent dekoracji), bliźniak jest ostry.
   */
  readonly urlStatyczny?: string;
}

/** Jedna dekoracja: co, gdzie i jak się rusza. */
export interface Dekoracja {
  readonly miejsce: Miejsce;
  readonly zasob: Zasob;
  readonly preset: Preset;
  readonly parametry: Required<ParametryAnimacji>;
}

/** Motyw po walidacji. */
export interface MotywSezonowy {
  readonly aktywny: boolean;
  readonly id: string;
  readonly nazwa: string;
  readonly stan: StanMotywu;
  /** Znaczniki czasu w milisekundach UTC. `null` = brak ograniczenia. */
  readonly odKiedy: number | null;
  readonly doKiedy: number | null;
  readonly rewizja: number;
  readonly zaktualizowano: number | null;
  readonly dekoracje: readonly Dekoracja[];
  /** Żółty pasek z tekstem okazji; `null` = zwykły pasek (koszyk, szkoła). */
  readonly pasek: PasekWiadomosci | null;
  /** Traktowanie typografii; `null` = bez zmian. */
  readonly typografia: Typografia | null;
  /** Paleta ekranu menu; `null` = barwy aplikacji. */
  readonly paleta: Paleta | null;
  /**
   * GRAF SCENY — jedyne wejście renderera (uniwersalny silnik). Schemat 3
   * podaje go wprost; konfiguracja silnika 1/2 jest do niego tłumaczona
   * (`scena.adapter.ts`), więc rysuje ją ten sam renderer.
   */
  readonly scena: Scena;
}

/** Co walidator odrzucił — do raportu, nie do interfejsu użytkownika. */
export interface RaportWalidacji {
  readonly odrzuconeDekoracje: number;
  readonly przycieteParametry: number;
  readonly powody: readonly string[];
}

export interface WynikWalidacji {
  readonly motyw: MotywSezonowy | null;
  readonly raport: RaportWalidacji;
}

/** Stan wyliczony z harmonogramu. */
export type StanHarmonogramu =
  | 'AKTYWNY'
  | 'PRZYSZLY'
  | 'WYGASLY'
  | 'WYLACZONY';

/**
 * ZDOLNOŚCI SILNIKA — to, co motyw może wymagać w `compatibility.requires`.
 * Zamknięta lista wyprowadzona z wyliczeń wyżej (nie ma tu nazw okazji).
 */
export const ZDOLNOSCI_SILNIKA: readonly string[] = [
  ...MIEJSCA,
  ...PRESETY,
  ...RODZAJE_ZASOBU,
  'messageBar',
  'messageBar:icon',
  'asset:staticUrl',
  // UNIWERSALNY SILNIK (schemat 3): regiony, typy warstw i efekty — każda
  // nazwa osobno, żeby skórka mogła wymagać DOKŁADNIE tego, czego używa.
  'scene',
  ...REGIONY.map((r) => 'region:' + r),
  ...TYPY_WARSTW.map((t) => 'layer:' + t),
  ...EFEKTY.map((e) => 'effect:' + e),
  'responsive',
  'background',
  'typography',
  ...TYPOGRAFIE.map((t) => 'typography:' + t),
  'palette',
  'hero',
  'effects',
];

/**
 * Jedyna dozwolona strategia awaryjna. Pole `fallback` istnieje w kontrakcie,
 * żeby backend mógł ją jawnie potwierdzić; każda inna wartość = motyw
 * odrzucony (a więc i tak zwykły interfejs).
 */
export const STRATEGIA_AWARYJNA = 'NORMAL_UI';

/** Sufit liczby zasobów w jednym motywie. */
// 2026-10-08: 32 (uniwersalny silnik — kilka grafik w wielu regionach).
// Bajtowy sufit całości (narzędzia: 2 MB) zostaje — więcej plików, nie cięższych.
export const MAX_ZASOBOW = 32;
